import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    label: String,
    delay: { type: Number, default: 1500 }
  }

  static SETTLE_TIMEOUT = 5000

  connect () {
    this.initialValues = new Map()
    this.captureInitial()
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd)
  }

  disconnect () {
    this.clearTimer()
    this.resolveInFlight()
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd)
  }

  submit (event) {
    const field = event && event.target
    if (field && field.type === 'file') {
      if (field.files && field.files.length > 0) {
        this.clearTimer()
        this.track()
      }
      return
    }
    this.clearTimer()
    this.flushTimer = setTimeout(() => this.flush(), this.delayValue)
  }

  flush () {
    this.clearTimer()
    if (!this.isDirty()) return
    if (typeof this.element.checkValidity === 'function' && !this.element.checkValidity()) {
      const invalid = this.element.querySelector(':invalid')
      this.notify(this.invalidMessage(invalid), 'error')
      if (invalid) invalid.focus()
      return
    }
    this.track()
  }

  submitNow () {
    this.clearTimer()
    if (typeof this.element.checkValidity === 'function' && !this.element.checkValidity()) return
    this.track()
  }

  settled () {
    if (this.flushTimer) this.flush()
    if (!this.inFlight) return Promise.resolve()

    return Promise.race([
      this.inFlight,
      new Promise((resolve) => setTimeout(resolve, this.constructor.SETTLE_TIMEOUT))
    ])
  }

  clearTimer () {
    clearTimeout(this.flushTimer)
    this.flushTimer = null
  }

  track () {
    if (!this.inFlight) {
      this.inFlight = new Promise((resolve) => { this.settleInFlight = resolve })
    }
    this.element.requestSubmit()
  }

  resolveInFlight () {
    this.settleInFlight?.()
    this.inFlight = null
    this.settleInFlight = null
  }

  isDirty () {
    return this.fields().some((f) => this.readValue(f) !== this.initialValues.get(f))
  }

  captureInitial () {
    this.fields().forEach((f) => this.initialValues.set(f, this.readValue(f)))
  }

  onSubmitEnd = (event) => {
    this.resolveInFlight()

    if (event.detail.success) {
      this.captureInitial()
      document.dispatchEvent(new CustomEvent('save:done'))
    } else {
      this.notify(`Couldn't save ${this.sectionName.toLowerCase()} — try again`, 'error')
    }
  }

  notify (message, variant) {
    document.dispatchEvent(new CustomEvent('toast:show', { detail: { message, variant } }))
  }

  fields () {
    return Array.from(this.element.querySelectorAll('input, textarea, select'))
      .filter((el) => !el.closest('[data-auto-save-ignore]'))
      .filter((el) => el.type !== 'hidden' && el.type !== 'submit' && el.type !== 'button' && el.type !== 'file')
  }

  readValue (field) {
    if (field.type === 'checkbox' || field.type === 'radio') return field.checked
    return field.value
  }

  // Private

  get sectionName () {
    return this.hasLabelValue ? this.labelValue : 'this'
  }

  invalidMessage (field) {
    const hint = field?.dataset.saveHint || field?.validationMessage
    return [this.labelFor(field), hint].filter(Boolean).join(' — ') || "Couldn't save — check the field"
  }

  labelFor (field) {
    if (!field) return null
    const label = field.labels?.[0] || this.element.querySelector(`label[for="${field.id}"]`)
    return label?.textContent.replace(/\s*\(optional\)\s*$/i, '').trim() || null
  }
}
